import { useCallback, useState } from 'react';

import { InquisitiveActivitiesResponseVariant } from 'global/constants';
import {
  INTERACTIVE_RICHTEXT_EXTENSIONS,
  ItemToPreview,
} from '../interactive-response-shared.constants';
import { LinkButton } from 'approot/shared/styles/buttons/link-button.style';
import { InteractiveResponseNavControls } from './interactive-response-controls';
import { ImageViewFill, ImageViewFit } from '../../image/image-print';
import { getRichtextValue } from '../../shared/logic/get-richtext-value';
import {
  isTextImageVariant,
  isTextVariant,
} from './interactive-response-shared.utils';
import * as Styled from './interactive-response-image-zoom.style';
import InteractiveResponseAudioButton from './interactive-response-audio-button';
import {
  SecondaryButton,
  SecondaryButtonIcon,
} from 'approot/shared/styles/buttons/secondary-button.style';
import IconDropdown from 'approot/shared/icons/icon-dropdown';

type Props = {
  itemToPreview: ItemToPreview;
  isNextDisabled: boolean;
  isPrevDisabled: boolean;
  variant: InquisitiveActivitiesResponseVariant;
  onNext: () => void;
  onPrevious: () => void;
  onBackClick: () => void;
};

const InteractiveResponseImageZoom = ({
  itemToPreview,
  isNextDisabled,
  isPrevDisabled,
  variant,
  onNext,
  onPrevious,
  onBackClick,
}: Props) => {
  const [isTextVisible, setIsTextVisible] = useState<boolean>(true);

  const itemVariant = itemToPreview.variant || variant;
  const imageContent = isTextVariant(
    itemVariant
  ) ? null : itemToPreview.isFit ? (
    <ImageViewFit
      imageUrl={itemToPreview.imageUrl}
      altText={itemToPreview.altText}
    />
  ) : (
    <ImageViewFill
      imageUrl={itemToPreview.imageUrl}
      altText={itemToPreview.altText}
    />
  );

  const toggleText = useCallback(() => {
    setIsTextVisible(!isTextVisible);
  }, [isTextVisible]);

  return (
    <Styled.InteractiveResponseZoomImage>
      <Styled.InteractiveResponseZoomImageInner>
        <Styled.InteractiveZoomContent>
          <LinkButton
            type="button"
            data-selector="back-btn"
            onClick={onBackClick}
          >
            Back
          </LinkButton>
        </Styled.InteractiveZoomContent>
        <Styled.InteractiveResponseImageZoomContainer
          $baseColour={itemToPreview.baseColour}
        >
          {isTextVariant(itemVariant) ? (
            <Styled.InteractiveZoomTextContent
              $baseColour={itemToPreview.baseColour}
              dangerouslySetInnerHTML={{
                __html: getRichtextValue(
                  itemToPreview.richtext,
                  INTERACTIVE_RICHTEXT_EXTENSIONS
                ),
              }}
            />
          ) : isTextImageVariant(itemVariant) ? (
            <>
              {imageContent}
              {itemToPreview.text && (
                <Styled.InteractiveZoomContainerWrapper>
                  <Styled.InteractiveZoomTextImageContainer
                    $isVisible={isTextVisible}
                  >
                    {itemToPreview.text}
                  </Styled.InteractiveZoomTextImageContainer>
                  <Styled.InteractiveZoomControlsContainer
                    $isVisible={isTextVisible}
                  >
                    {isTextVisible ? (
                      <SecondaryButtonIcon
                        type="button"
                        aria-label="Hide Text"
                        $borderRadius={40}
                        onClick={toggleText}
                      >
                        <IconDropdown width={16} height={16} />
                      </SecondaryButtonIcon>
                    ) : (
                      <SecondaryButton
                        type="button"
                        $borderRadius={40}
                        onClick={toggleText}
                      >
                        Show Text
                      </SecondaryButton>
                    )}
                  </Styled.InteractiveZoomControlsContainer>
                </Styled.InteractiveZoomContainerWrapper>
              )}
            </>
          ) : (
            imageContent
          )}

          {itemToPreview.audioUrl && (
            <Styled.InteractiveZoomContainer>
              <InteractiveResponseAudioButton
                withTooltip
                itemId={itemToPreview.itemId}
                description={itemToPreview.altText}
              />
            </Styled.InteractiveZoomContainer>
          )}
        </Styled.InteractiveResponseImageZoomContainer>
        <InteractiveResponseNavControls
          onNext={onNext}
          onPrevious={onPrevious}
          isNextDisabled={isNextDisabled}
          isPrevDisabled={isPrevDisabled}
        />
      </Styled.InteractiveResponseZoomImageInner>
    </Styled.InteractiveResponseZoomImage>
  );
};

export default InteractiveResponseImageZoom;
